import React, { useState, useEffect } from 'react';
import EvaluationLayout from '../../components/EvaluationLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import Button from '../../components/ui/button';
import Input from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import Badge from '../../components/ui/badge';
import {
  Settings,
  Key,
  Clock,
  Eye,
  EyeOff,
  TestTube,
  Plus,
  Trash2,
  AlertCircle
} from 'lucide-react';
import axios from '../../utils/axios';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';


// Centralized API error extractor to surface exact messages
const extractApiError = (error) => {
  try {
    const data = error?.response?.data;
    if (!data) return error?.message || 'An error occurred';
    // Common patterns
    // 1) Our backend: { success:false, message:'...', data:{ error:'...' }}
    if (typeof data?.data?.error === 'string' && data.data.error.trim()) return data.data.error;
    // 1b) Nested details inside data.data
    if (typeof data?.data?.details?.error === 'string' && data.data.details.error.trim()) return data.data.details.error;
    if (typeof data?.data?.details?.message === 'string' && data.data.details.message.trim()) return data.data.details.message;
    // 2) Our backend plain error: { error:'...' }
    if (typeof data?.error === 'string' && data.error.trim()) return data.error;
    // 3) Our backend message
    if (typeof data?.message === 'string' && data.message.trim()) return data.message;
    // 4) OpenAI style: { error: { message: '...' } }
    if (typeof data?.error?.message === 'string' && data.error.message.trim()) return data.error.message;
    // 5) Nested details
    if (typeof data?.details?.error === 'string' && data.details.error.trim()) return data.details.error;
    if (typeof data?.details?.message === 'string' && data.details.message.trim()) return data.details.message;
    return error?.message || 'An error occurred';
  } catch (_) {
    return error?.message || 'An error occurred';
  }
};

// Helper functions for model configuration
const getDefaultModelPlaceholder = (provider) => {
  const placeholders = {
    openai: 'gpt-4, gpt-3.5-turbo, gpt-4-turbo',
    anthropic: 'claude-3-opus, claude-3-sonnet, claude-3-haiku',
    groq: 'llama2-70b-4096, mixtral-8x7b-32768',
    mistral: 'mistral-large, mistral-medium, mistral-small',
    custom: 'your-custom-model-name'
  };
  return placeholders[provider] || 'model-name';
};

const getModelHelpText = (provider) => {
  const helpTexts = {
    openai: 'Popular models: gpt-4, gpt-3.5-turbo, gpt-4-turbo-preview',
    anthropic: 'Available models: claude-3-opus-20240229, claude-3-sonnet-20240229, claude-3-haiku-20240307',
    groq: 'Supported models: llama2-70b-4096, mixtral-8x7b-32768, gemma-7b-it',
    mistral: 'Available models: mistral-large-latest, mistral-medium-latest, mistral-small-latest',
    custom: 'Enter the exact model name as supported by your API endpoint'
  };
  return helpTexts[provider] || 'Enter the model name supported by this provider';
};

const getApiKeyPlaceholder = (provider) => {
  const placeholders = {
    openai: 'sk-...',
    anthropic: 'sk-ant-...',
    groq: 'gsk_...',
    mistral: 'your-mistral-api-key',
    custom: 'your-api-key'
  };
  return placeholders[provider] || 'your-api-key';
};

const getApiKeyHelpText = (provider) => {
  const helpTexts = {
    openai: 'Get your API key from https://platform.openai.com/api-keys',
    anthropic: 'Get your API key from https://console.anthropic.com/',
    groq: 'Get your API key from https://console.groq.com/keys',
    mistral: 'Get your API key from https://console.mistral.ai/',
    custom: 'Enter the API key for your custom provider'
  };
  return helpTexts[provider] || 'Enter your API key for this provider';
};

const APIConfiguration = () => {
  const { user, loading: authLoading } = useAuth();
  const [providers, setProviders] = useState({});
  const [configurations, setConfigurations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});
  const [showCredentials, setShowCredentials] = useState({});
  const [selectedProvider, setSelectedProvider] = useState('');
  const [formData, setFormData] = useState({
    provider: '',
    credentials: {},
    configuration: {},
    is_active: true
  });
  const [errors, setErrors] = useState({});
  const [editingConfig, setEditingConfig] = useState(null);


  useEffect(() => {
    if (!authLoading && user) {
      fetchData();
    }
  }, [authLoading, user]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const [providersResponse, configurationsResponse] = await Promise.all([
        axios.get('/llm-providers/available/list'),
        axios.get('/llm-providers')
      ]);

      const providersData = providersResponse.data.data || {};
      const configurations = configurationsResponse.data.data || [];

      // Convert backend provider format to frontend format
      const availableProviders = {};
      Object.entries(providersData).forEach(([key, providerInfo]) => {
        availableProviders[key] = providerInfo.name || key;
      });

      setProviders(availableProviders);
      setConfigurations(configurations);
    } catch (error) {
      console.error('Failed to fetch API configuration data:', error);
      toast.error('Failed to load API configuration data');

      // Fallback to empty state
      setProviders({});
      setConfigurations([]);
    } finally {
      setLoading(false);
    }
  };



  const handleProviderSelect = (providerKey) => {
    setSelectedProvider(providerKey);

    // Find existing configuration for this provider
    const existingConfig = configurations.find(config => config.provider === providerKey);

    if (existingConfig) {
      setEditingConfig(existingConfig.id);
      setFormData({
        provider: providerKey,
        credentials: {
          // Leave empty for existing configs - user can optionally update
          api_key: ''
        },
        configuration: existingConfig.configuration || {},
        is_active: existingConfig.is_active,
        _hasExistingCredentials: existingConfig.has_credentials
      });
    } else {
      setEditingConfig(null);
      setFormData({
        provider: providerKey,
        credentials: {},
        configuration: {},
        is_active: true,
        _hasExistingCredentials: false
      });
    }

    setErrors({});
  };



  const testCredentials = async () => {
    if (!selectedProvider) {
      toast.error('Please select a provider');
      return;
    }

    const isUpdatingExisting = editingConfig && formData._hasExistingCredentials;
    const hasNewApiKey = formData.credentials.api_key && formData.credentials.api_key.trim() !== '';

    // For new configs, require API key. For existing configs, allow testing with existing credentials
    if (!isUpdatingExisting && !hasNewApiKey) {
      toast.error('Please enter an API key');
      return;
    }

    const testKey = `${selectedProvider}_test`;
    setTesting(prev => ({ ...prev, [testKey]: true }));

    try {
      if (isUpdatingExisting && !hasNewApiKey) {
        // Test existing configuration without saving new credentials
        const testResponse = await axios.post(`/llm-providers/${editingConfig}/test`, {});

        if (testResponse.data.success) {
          toast.success('Connection test successful with existing credentials!');
          await fetchData();
        } else {
          const data = testResponse.data || {};
          const msg = (data.data && (data.data.error || data.data.details?.error || data.data.details?.message)) || data.error || data.message || 'Connection test failed';
          toast.error(msg);
        }
      } else {
        // Save configuration and test (for new configs or when updating credentials)
        const requestData = {
          provider: selectedProvider,
          configuration: formData.configuration,
          is_active: formData.is_active ?? true
        };

        // Include credentials if provided
        if (hasNewApiKey) {
          requestData.credentials = {
            api_key: formData.credentials.api_key.trim()
          };
        }

        const saveResponse = await axios.post('/llm-providers', requestData);

        if (!saveResponse.data?.data?.id) {
          throw new Error('Failed to save configuration');
        }

        const configId = saveResponse.data.data.id;

        // Then test the connection with the saved configuration
        const testResponse = await axios.post(`/llm-providers/${configId}/test`, {});

        if (testResponse.data.success) {
          toast.success('Connection test successful! Configuration saved.');
          await fetchData();
        } else {
          const data = testResponse.data || {};
          const msg = (data.data && (data.data.error || data.data.details?.error || data.data.details?.message)) || data.error || data.message || 'Connection test failed';
          toast.error(msg);
        }
      }
    } catch (error) {
      console.error('Test connection error:', error);
      const errorMessage = extractApiError(error);
      toast.error(errorMessage);
    } finally {
      setTesting(prev => ({ ...prev, [testKey]: false }));
    }
  };

  const saveConfiguration = async () => {
    if (!selectedProvider) {
      toast.error('Please select a provider');
      return;
    }

    // For existing configurations, allow saving without changing API key
    const isUpdatingExisting = editingConfig && formData._hasExistingCredentials;
    const apiKeyValue = formData.credentials?.api_key || '';
    const hasNewApiKey = apiKeyValue.trim() !== '';

    // For new configs, API key is required
    if (!isUpdatingExisting && !hasNewApiKey) {
      toast.error('Please enter an API key');
      return;
    }

    setSaving(true);
    setErrors({});

    try {
      const requestData = {
        provider: selectedProvider,
        configuration: formData.configuration || {},
        is_active: formData.is_active !== false // Default to true
      };

      // Only include credentials if user provided a new API key
      if (hasNewApiKey) {
        requestData.credentials = {
          api_key: apiKeyValue.trim()
        };
      }

      console.log('[API Config] Saving configuration:', {
        isUpdatingExisting,
        hasNewApiKey,
        provider: selectedProvider,
        apiKeyLength: hasNewApiKey ? apiKeyValue.trim().length : 0,
        hasCredentialsInRequest: !!requestData.credentials
      });

      const response = await axios.post('/llm-providers', requestData);

      console.log('[API Config] Save response:', response.data);

      if (response.data && response.data.success) {
        toast.success(response.data.message || 'Configuration saved successfully');
        await fetchData(); // Refresh the data

        // Reset form
        setSelectedProvider('');
        setFormData({
          provider: '',
          credentials: {},
          configuration: {},
          is_active: true,
          _hasExistingCredentials: false
        });
        setEditingConfig(null);
      } else {
        const errorMsg = response.data?.message || 'Failed to save configuration';
        console.error('[API Config] Save failed:', errorMsg);
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('[API Config] Save error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });

      const errorMessage = extractApiError(error);
      toast.error(`Configuration not saved: ${errorMessage}`);

      if (error.response?.data?.errors) {
        console.error('[API Config] Validation errors:', error.response.data.errors);
        setErrors(error.response.data.errors);
      }
    } finally {
      setSaving(false);
    }
  };

  const deleteConfiguration = async (configId, providerName) => {
    if (!window.confirm(`Are you sure you want to delete the ${providerName} configuration?`)) {
      return;
    }

    try {
      const response = await axios.delete(`/llm-providers/${configId}`);

      if (response.data.success) {
        toast.success(response.data.message);
        await fetchData(); // Refresh the data

        // Reset form if we were editing this config
        if (editingConfig === configId) {
          setSelectedProvider('');
          setFormData({
            provider: '',
            credentials: {},
            configuration: {},
            is_active: true
          });
          setEditingConfig(null);
        }
      }
    } catch (error) {
      const errorMessage = extractApiError(error) || 'Failed to delete configuration';
      toast.error(errorMessage);
    }
  };

  const testExistingConnection = async (configId, providerName) => {
    const testKey = `${configId}_existing`;
    setTesting(prev => ({ ...prev, [testKey]: true }));

    try {
      const response = await axios.post(`/llm-providers/${configId}/test`);

      if (response.data.success) {
        toast.success(`${providerName} connection test successful!`);
        await fetchData(); // Refresh to update test status
      } else {
        const data = response.data || {};
        console.debug('LLM test (existing) non-success response:', data);
        const msg = (data.data && (data.data.error || data.data.details?.error || data.data.details?.message)) || data.error || data.message || `${providerName || 'Provider'} connection test failed`;
        const docs = data.docs_url || (data.data && data.data.docs_url);
        toast.error(
          <div className="max-w-md">
            <p className="text-sm break-words whitespace-pre-wrap">{msg}</p>
            {(!data.data?.error && !data.error && !data.message && data) && (
              <pre className="text-xs mt-2 whitespace-pre-wrap break-words bg-gray-50 p-2 rounded border">{JSON.stringify(data, null, 2)}</pre>
            )}
            {docs && (
              <a href={docs} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline text-sm block mt-2">View documentation</a>
            )}
          </div>,
          { duration: 10000, style: { maxWidth: '500px' } }
        );
      }
    } catch (error) {
      const errorMessage = extractApiError(error) || 'Connection test failed';
      toast.error(errorMessage);
    } finally {
      setTesting(prev => ({ ...prev, [testKey]: false }));
    }
  };

  const toggleConfigurationStatus = async (configId, currentStatus, providerName) => {
    try {
      const response = await axios.post(`/llm-providers/${configId}/toggle-status`);

      if (response.data.success) {
        toast.success(response.data.message);
        await fetchData(); // Refresh the data
      }
    } catch (error) {
      const errorMessage = extractApiError(error) || 'Failed to toggle status';
      toast.error(errorMessage);
    }
  };



  const getStatusBadge = (config) => {
    if (!config.is_active) {
      return <Badge variant="secondary">Inactive</Badge>;
    }

    switch (config.test_status) {
      case 'success':
        return <Badge variant="success" className="bg-green-100 text-green-800">Connected</Badge>;
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>;
      case 'pending':
        return <Badge variant="secondary">Testing</Badge>;
      default:
        return <Badge variant="secondary">Not Tested</Badge>;
    }
  };



  // Show loading while checking authentication
  if (authLoading) {
    return (
      <EvaluationLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          <span className="ml-2">Loading...</span>
        </div>
      </EvaluationLayout>
    );
  }

  // Show login prompt if not authenticated
  if (!user) {
    return (
      <EvaluationLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center space-y-4">
            <AlertCircle className="h-12 w-12 text-yellow-500 mx-auto" />
            <h2 className="text-xl font-semibold">Authentication Required</h2>
            <p className="text-gray-600">Please log in to access API configuration.</p>
            <Link to="/login" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
              Go to Login
            </Link>
          </div>
        </div>
      </EvaluationLayout>
    );
  }

  if (loading) {
    return (
      <EvaluationLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          <span className="ml-2">Loading API configuration...</span>
        </div>
      </EvaluationLayout>
    );
  }

  return (
    <EvaluationLayout>
      <div className="space-y-8 w-full">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Settings className="h-6 w-6" />
            API Configuration
          </h1>
          <p className="text-gray-600">Configure your LLM provider credentials and settings for evaluations</p>
        </div>



        {/* Existing Configurations */}
        <Card>
          <CardHeader>
            <CardTitle>Configured Providers</CardTitle>
            <CardDescription>
              Manage your existing LLM provider configurations
            </CardDescription>
          </CardHeader>
          <CardContent>
            {configurations.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Key className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No providers configured yet</p>
                <p className="text-sm">Add your first provider configuration below</p>
              </div>
            ) : (
              <div className="space-y-4">
                {configurations.map((config) => (
                  <div
                    key={config.id}
                    className="border rounded-lg p-4 space-y-3 hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleProviderSelect(config.provider)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <h3 className="font-medium">{providers[config.provider] || config.provider}</h3>
                        {getStatusBadge(config)}
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); testExistingConnection(config.id, providers[config.provider]); }}
                          disabled={testing[`${config.id}_existing`]}
                        >
                          {testing[`${config.id}_existing`] ? (
                            <Clock className="h-4 w-4 animate-spin" />
                          ) : (
                            <TestTube className="h-4 w-4" />
                          )}
                          Test
                        </Button>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handleProviderSelect(config.provider); }}
                        >
                          Edit
                        </Button>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); toggleConfigurationStatus(config.id, config.is_active, providers[config.provider]); }}
                        >
                          {config.is_active ? 'Disable' : 'Enable'}
                        </Button>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); deleteConfiguration(config.id, providers[config.provider]); }}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {config.last_tested && (
                      <p className="text-sm text-gray-500">
                        Last tested: {new Date(config.last_tested).toLocaleString()}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Provider Selection */}
        <Card>
          <CardHeader>
            <CardTitle>Add New Provider</CardTitle>
            <CardDescription>
              Configure a new LLM provider for evaluations
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Provider</label>
              <Select value={selectedProvider} onValueChange={handleProviderSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose an LLM provider" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(providers).map(([key, name]) => (
                    <SelectItem key={key} value={key}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Configuration Form */}
        {selectedProvider && providers[selectedProvider] && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                Configure {providers[selectedProvider]}
                {editingConfig && <Badge variant="secondary">Editing</Badge>}
              </CardTitle>
              <CardDescription>
                Enter your API credentials and configuration settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-8 p-8">
              {/* Credentials Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium border-b pb-2">API Credentials</h3>

                {/* API Key field (common to all providers) */}
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <Key className="h-4 w-4" />
                    API Key *
                    {editingConfig && formData._hasExistingCredentials && (
                      <Badge variant="secondary" className="ml-2 text-xs">
                        Configured
                      </Badge>
                    )}
                  </label>
                  <div className="relative">
                    <Input
                      type={showCredentials.api_key ? 'text' : 'password'}
                      value={formData.credentials.api_key || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        credentials: {
                          ...prev.credentials,
                          api_key: e.target.value
                        }
                      }))}
                      placeholder={editingConfig && formData._hasExistingCredentials
                        ? 'Leave empty to keep existing key, or enter new key'
                        : getApiKeyPlaceholder(selectedProvider)}
                      className={errors['credentials.api_key'] ? 'border-red-500' : ''}
                    />
                    <button
                      type="button"
                      onClick={() => setShowCredentials(prev => ({ ...prev, api_key: !prev.api_key }))}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showCredentials.api_key ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {errors['credentials.api_key'] && (
                    <p className="text-red-500 text-sm">{errors['credentials.api_key']}</p>
                  )}
                  <p className="text-xs text-gray-500">
                    {editingConfig && formData._hasExistingCredentials
                      ? 'API key is already configured. Leave empty to keep existing credentials, or enter a new key to update.'
                      : getApiKeyHelpText(selectedProvider)}
                  </p>
                </div>

                {/* Additional credential fields for custom providers */}
                {selectedProvider === 'custom' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Organization ID (Optional)</label>
                    <Input
                      value={formData.credentials.organization_id || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        credentials: {
                          ...prev.credentials,
                          organization_id: e.target.value
                        }
                      }))}
                      placeholder="org-your-organization-id"
                    />
                    <p className="text-xs text-gray-500">
                      Organization ID if required by your provider
                    </p>
                  </div>
                )}
              </div>

              {/* Configuration Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium border-b pb-2">Configuration Settings</h3>

                {/* Default Model */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Default Model</label>
                  <Input
                    value={formData.configuration.default_model || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      configuration: {
                        ...prev.configuration,
                        default_model: e.target.value
                      }
                    }))}
                    placeholder={getDefaultModelPlaceholder(selectedProvider)}
                  />
                  <p className="text-xs text-gray-500">
                    {getModelHelpText(selectedProvider)}
                  </p>
                </div>

                {/* Custom Models */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Custom Models (Optional)</label>
                  <textarea
                    value={formData.configuration.custom_models || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      configuration: {
                        ...prev.configuration,
                        custom_models: e.target.value
                      }
                    }))}
                    placeholder="Enter custom model names, one per line"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500">
                    Add custom or fine-tuned models available in your account
                  </p>
                </div>

                {/* Base URL (for custom providers) */}
                {selectedProvider === 'custom' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Base URL</label>
                    <Input
                      value={formData.configuration.base_url || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        configuration: {
                          ...prev.configuration,
                          base_url: e.target.value
                        }
                      }))}
                      placeholder="https://api.your-provider.com/v1"
                    />
                    <p className="text-xs text-gray-500">
                      API base URL for your custom provider
                    </p>
                  </div>
                )}

                {/* Advanced Configuration */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Timeout (seconds)</label>
                    <Input
                      type="number"
                      min="1"
                      max="300"
                      value={formData.configuration.timeout || 30}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        configuration: {
                          ...prev.configuration,
                          timeout: parseInt(e.target.value)
                        }
                      }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Max Retries</label>
                    <Input
                      type="number"
                      min="0"
                      max="10"
                      value={formData.configuration.max_retries || 3}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        configuration: {
                          ...prev.configuration,
                          max_retries: parseInt(e.target.value)
                        }
                      }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Rate Limit (req/min)</label>
                    <Input
                      type="number"
                      min="1"
                      max="1000"
                      value={formData.configuration.rate_limit || 60}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        configuration: {
                          ...prev.configuration,
                          rate_limit: parseInt(e.target.value)
                        }
                      }))}
                    />
                  </div>
                </div>
              </div>

              {/* Active Status */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  className="rounded"
                />
                <label htmlFor="is_active" className="text-sm font-medium">
                  Enable this configuration
                </label>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  onClick={testCredentials}
                  variant="outline"
                  disabled={testing[`${selectedProvider}_test`] || (!formData.credentials.api_key?.trim() && !formData._hasExistingCredentials)}
                >
                  {testing[`${selectedProvider}_test`] ? (
                    <Clock className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <TestTube className="h-4 w-4 mr-2" />
                  )}
                  Test Connection
                </Button>

                <Button
                  onClick={saveConfiguration}
                  disabled={saving || (!formData.credentials.api_key?.trim() && !formData._hasExistingCredentials)}
                >
                  {saving ? (
                    <Clock className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Plus className="h-4 w-4 mr-2" />
                  )}
                  {editingConfig ? 'Update Configuration' : 'Save Configuration'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </EvaluationLayout>
  );
};

export default APIConfiguration;
